import React from 'react';
import { CheckCircle, AlertCircle, X, Clock, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  icon?: React.ReactNode;
  autoClose?: boolean;
  onClose: () => void;
}

export function Toast({ message, type, icon, autoClose = true, onClose }: ToastProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  
  // Ajouter un log pour déboguer
  React.useEffect(() => {
    // console.log supprimé
  }, [message, type]);

  React.useEffect(() => {
    if (!autoClose) return;
    
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose, autoClose]);

  const getIconColor = () => {
    switch (type) {
      case 'success':
        return 'text-green-500 dark:text-green-400';
      case 'error':
        return 'text-red-500 dark:text-red-400';
      case 'info':
        return 'text-blue-500 dark:text-blue-400';
      default:
        return 'text-green-500 dark:text-green-400';
    }
  };

  const getDefaultIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className={`h-5 w-5 ${getIconColor()}`} />;
      case 'error':
        return <AlertCircle 
          className="h-5 w-5 dark:text-state-error" 
          style={{ 
            color: theme === 'light' ? '#dc2626' : undefined,
            stroke: theme === 'light' ? '#dc2626' : undefined 
          }}
        />;
      case 'info':
        return <AlertCircle 
          className="h-5 w-5 dark:text-blue-400" 
          style={{ 
            color: theme === 'light' ? '#3b82f6' : undefined,
            stroke: theme === 'light' ? '#3b82f6' : undefined 
          }}
        />;
      default:
        return <CheckCircle className={`h-5 w-5 ${getIconColor()}`} />;
    }
  };

  // S'assurer que le message est toujours affiché, même s'il est vide ou null
  const displayMessage = message || (type === 'error' ? t('common.error') : t('common.success'));

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg p-4 shadow-lg bg-surface text-content-primary max-w-md border border-gray-200 dark:border-white/10`}
      >
        {icon || getDefaultIcon()}
        <p className="text-sm break-words">{displayMessage}</p>
        <button
          onClick={onClose}
          className="ml-2 rounded-full p-1 hover:bg-content-primary/10 transition-colors"
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4 text-content-secondary hover:text-content-primary transition-colors" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
